import { useMemo, useState } from 'react'
import { Modal, Button, Input, Select, DateInput } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { ExpenseAttachments } from '@/features/expenses/components/ExpenseAttachments'
import { getTodayDateString } from '@/features/expenses/schemas/expense.schema'
import type { ExpenseAttachment } from '@/features/expenses/types/expense.types'
import { useMyClaimOptions, useSubmitClaim } from '../hooks/useExpenseClaims'
import { validateClaimForm, type ClaimFormErrors } from '../utils/claimForm'

interface ClaimFormModalProps {
  open: boolean
  onClose: () => void
}

const TEXTAREA_CLASS =
  'box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500'

/**
 * Submit an expense claim with its receipts. The currency is the claimant's
 * pay currency for the chosen company (claims are paid back with salary), so
 * it is shown, not chosen.
 */
export function ClaimFormModal({ open, onClose }: ClaimFormModalProps): React.ReactElement | null {
  // Mounted only while open, so every opening starts from an empty form.
  return open ? <ClaimFormBody onClose={onClose} /> : null
}

function ClaimFormBody({ onClose }: { onClose: () => void }): React.ReactElement {
  const { data: options, isLoading: optionsLoading } = useMyClaimOptions()
  const submitMutation = useSubmitClaim()

  const [companyId, setCompanyId] = useState('')
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(getTodayDateString())
  const [description, setDescription] = useState('')
  const [receipts, setReceipts] = useState<ExpenseAttachment[]>([])
  const [errors, setErrors] = useState<ClaimFormErrors>({})

  const companies = useMemo(() => options?.companies ?? [], [options])
  // One company: preselect it, so most staff never touch the field.
  const effectiveCompanyId = companyId || (companies.length === 1 ? companies[0].id : '')
  const company = companies.find((c) => c.id === effectiveCompanyId)

  const companyOptions: SelectOption[] = companies.map((c) => ({ value: c.id, label: c.name }))
  const categoryOptions: SelectOption[] = (options?.categories ?? []).map((c) => ({
    value: c.value,
    label: c.label,
  }))

  const handleSubmit = (): void => {
    const values = {
      companyId: effectiveCompanyId,
      title,
      category,
      amount,
      date,
      description,
      receiptIds: receipts.map((r) => r.id),
    }
    const found = validateClaimForm(values, getTodayDateString())
    setErrors(found)
    if (Object.keys(found).length > 0 || !company) return
    submitMutation.mutate(
      {
        companyId: effectiveCompanyId,
        title: title.trim(),
        category,
        amount: Number(amount),
        currency: company.currency,
        date,
        ...(description.trim() ? { description: description.trim() } : {}),
        receiptIds: values.receiptIds,
      },
      { onSuccess: () => onClose() }
    )
  }

  const noCompanies = !optionsLoading && companies.length === 0

  return (
    <Modal
      open
      onClose={onClose}
      title="Submit an expense claim"
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitMutation.isPending}
            disabled={noCompanies || optionsLoading}
          >
            Submit claim
          </Button>
        </div>
      }
    >
      {noCompanies ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You are not an active member of any company, so there is no payroll to pay a claim back
          through. Ask your administrator.
        </p>
      ) : (
        <div className="space-y-4">
          <Select
            label="Company"
            options={companyOptions}
            value={effectiveCompanyId}
            onChange={setCompanyId}
            placeholder={optionsLoading ? 'Loading…' : 'Select company…'}
            error={errors.companyId}
            required
          />
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Taxi to client site"
            maxLength={120}
            error={errors.title}
            required
          />
          <Select
            label="Category"
            options={categoryOptions}
            value={category}
            onChange={setCategory}
            placeholder="Select category…"
            error={errors.category}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={`Amount${company ? ` (${company.currency})` : ''}`}
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={errors.amount}
              hint={company ? `Paid back in ${company.currency} with your salary` : undefined}
              required
            />
            <DateInput
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              error={errors.date}
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="claim-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Notes
            </label>
            <textarea
              id="claim-description"
              rows={3}
              value={description}
              maxLength={1000}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: what the expense was for"
              className={TEXTAREA_CLASS}
            />
            {errors.description && (
              <p className="text-xs text-red-600 dark:text-red-400">{errors.description}</p>
            )}
          </div>
          <ExpenseAttachments
            value={receipts}
            onChange={setReceipts}
            disabled={submitMutation.isPending}
          />
          {errors.receiptIds && (
            <p className="text-xs text-red-600 dark:text-red-400">{errors.receiptIds}</p>
          )}
        </div>
      )}
    </Modal>
  )
}

ClaimFormModal.displayName = 'ClaimFormModal'
